import {
  AnyAction, combineReducers, configureStore, PreloadedState, Reducer,
} from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import {
  createMigrate,
  createTransform,
  FLUSH,
  MigrationManifest,
  PAUSE,
  PERSIST,
  PersistConfig,
  PersistedState,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import * as Sentry from '@sentry/react';
import onboardingReducer from './slices/onboardingSlice';
import dashboardReducer from './slices/dashboardSlice';
import contentReducer from './slices/contentSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import coachReducer from './slices/coachSlice';

// The upgrade request fields are transient lifecycle state and must not be
// persisted: persisting 'success' would replay the post-upgrade confetti on
// every subsequent visit. Only the durable plan/onboarding flags are persisted.
const subscriptionPersistConfig = {
  key: 'subscription',
  storage,
  blacklist: ['upgradeStatus', 'upgradeError', 'upgradeErrorCode'],
};

// The slices covered by the root persistConfig below. Named separately so the SN-936 migration can
// walk them without duplicating the list.
const persistedSliceReducers: Record<string, Reducer> = {
  onboarding: onboardingReducer,
  dashboard: dashboardReducer,
  content: contentReducer,
};

const appReducer = combineReducers({
  onboarding: onboardingReducer,
  dashboard: dashboardReducer,
  content: contentReducer,
  subscription: persistReducer(subscriptionPersistConfig, subscriptionReducer),
  coach: coachReducer,
});

const rootReducer = (state: ReturnType<typeof appReducer> | undefined, action: AnyAction) => {
  if (action.type === 'logout/LOGOUT') {
    // for all keys defined in persistConfig(s)
    storage.removeItem('persist:root');
    storage.removeItem('persist:subscription');

    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

export type RootState = ReturnType<typeof rootReducer>

// Fields typed `T[] | undefined`. Since they start out undefined they cannot be recognised as
// lists by inspecting initial state, so they have to be named here. Everything that defaults to
// `[]` is picked up automatically by listFieldsOf below.
const OPTIONAL_LIST_FIELDS: Record<string, string[]> = {
  dashboard: [
    'companyPartnerRelations',
    'allActivePartners',
    'recentRebatePartners',
    'partnersByCategories',
    'categories',
  ],
  onboarding: ['partnersInformationQuestions'],
};

const PROBE_ACTION = { type: '@@migration/PROBE' };

// The list-typed fields of a slice, read off its own initial state so new fields are covered
// without editing this file.
const listFieldsOf = (sliceName: string, reducer: Reducer): string[] => {
  const initialState = reducer(undefined, PROBE_ACTION) as Record<string, unknown>;
  return [
    ...Object.keys(initialState).filter((key) => Array.isArray(initialState[key])),
    ...(OPTIONAL_LIST_FIELDS[sliceName] ?? []),
  ];
};

// Use migrations if making changes (adding/removing fields, changing data types)
// to the redux store object at more than 2 levels deep.
// NOTE: keys must be numeric literals. createMigrate runs them through parseInt, so a bare
// identifier key (e.g. `LATEST_MIGRATION_VERSION:`) becomes the string "LATEST_MIGRATION_VERSION",
// parses to NaN, and the migration is silently skipped.
const LATEST_MIGRATION_VERSION = 1; // previous version is 0
const migrations: MigrationManifest = {
  // SN-936: a response with the wrong shape could be persisted into any list-typed field, and
  // because the value rehydrated on every load the first `.map`/`.find`/`.includes` on it threw
  // during render and blanked the page. Refreshing replayed the same broken value, so the only way
  // out was logging out (which clears persist:root). Drop the persisted lists once so the normal
  // thunks refetch them and a refresh recovers.
  1: (state: PersistedState) => {
    const root = state as unknown as Record<string, unknown> & PersistedState;
    if (!root) {
      return state;
    }

    const migrated = { ...root };

    Object.entries(persistedSliceReducers).forEach(([sliceName, reducer]) => {
      const persistedSlice = migrated[sliceName];
      if (!persistedSlice || typeof persistedSlice !== 'object') {
        return;
      }

      const slice = { ...persistedSlice as Record<string, unknown> };
      // Removing the key (rather than setting []) lets each field fall back to its own initial
      // value, so "not fetched yet" stays distinguishable from "fetched and empty".
      listFieldsOf(sliceName, reducer).forEach((field) => {
        delete slice[field];
      });
      migrated[sliceName] = slice;
    });

    return migrated as unknown as PersistedState;
  },
};

// SN-190: SN's master/sub-account access grants (isMasterAccount, myPurchasesAccess,
// historicRebateAccess) must never be trusted from a cached copy — only ever from a
// fresh membership-check API response. Stripped both on the way into storage (so a
// stale grant can't outlive a permission change) and on the way out (so anyone who
// already has one persisted from before this fix falls back to "no access" until the
// next fetch, rather than an unrefreshed, overly-permissive stale flag rehydrating).
const SN_ACCESS_FIELDS = ['isMasterAccount', 'myPurchasesAccess', 'historicRebateAccess'];

const stripSnAccessFlags = (state: unknown) => {
  const root = state as { memberInformation?: Record<string, unknown> } | undefined;
  if (!root?.memberInformation) return state;
  const memberInformation = { ...root.memberInformation };
  SN_ACCESS_FIELDS.forEach((field) => delete memberInformation[field]);
  return { ...root, memberInformation };
};

const stripSnAccessFlagsTransform = createTransform(
  (inboundState, key) => (key === 'onboarding' ? stripSnAccessFlags(inboundState) : inboundState),
  (outboundState, key) => (key === 'onboarding' ?
    stripSnAccessFlags(outboundState) : outboundState),
);

const persistConfig: PersistConfig<RootState> = {
  key: 'root',
  storage,
  // subscription has its own nested persist config (see subscriptionPersistConfig)
  // so it must be excluded here to avoid the root persisting a duplicate copy
  // that would re-include the transient upgrade fields.
  // coach state is session-only (welcome banner shown once per session)
  blacklist: ['subscription', 'coach'],
  transforms: [stripSnAccessFlagsTransform],
  // Shallow merge at 2 levels (https://github.com/rt2zz/redux-persist#state-reconciler)
  // No need for migration up to 2 levels deep in the store object
  // since this automatically handles reconcilation
  stateReconciler: autoMergeLevel2,
  // use migrations for changes at more than 2 levels deep in the store object
  migrate: createMigrate(migrations, {
    debug:
      process.env.REACT_APP_STORE_MIGRATION_DEBUG === 'true',
  }),
  version: LATEST_MIGRATION_VERSION,
};

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
});

// persist state on page refresh
const persistedReducer = persistReducer(persistConfig, rootReducer);

export function setupStore(preloadedState?: PreloadedState<RootState>) {
  return configureStore({
    reducer: persistedReducer, // used persistedReducer to persist state while store configuration
    preloadedState,
    devTools: process.env.ENV !== 'prod',
    // added middleware to avoid the error 'a non-serializable value was detected in the state'
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
    enhancers: (defaultEnhancers) => defaultEnhancers.concat([sentryReduxEnhancer]),
  });
}

export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = AppStore['dispatch']
